'use client';

import { useState } from 'react';
import { X, User, Lock, AlertCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminAuth } from '@/lib/supabase';

interface AdminLoginProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (adminData: any) => void;
}

const AdminLogin = ({ isOpen, onClose, onLogin }: AdminLoginProps): JSX.Element | null => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const adminData = await adminAuth.signIn(email, password);
      onLogin(adminData);
    } catch (err: any) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-3 rounded-xl">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                Admin Access
              </h2>
              <p className="text-white/90">Mountain View Management</p>
            </div>
          </div>
        </div>

  

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className={`flex items-center space-x-2 p-3 border rounded-lg ${
              error.includes('created') 
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#333333] font-medium flex items-center">
              <User className="h-4 w-4 mr-2 text-[#7C8F75]" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20"
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#333333] font-medium flex items-center">
              <Lock className="h-4 w-4 mr-2 text-[#7C8F75]" />
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20"
              placeholder="Enter your password"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] hover:from-[#7C8F75]/90 hover:to-[#A8B89A]/90 text-white py-3 text-lg font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg"
          >
            {isLoading 
              ? 'Signing In...' 
              : 'Access Dashboard'
            }
          </Button>

          <div className="text-center">
            <p className="text-sm text-[#333333]/60">
              Access the Mountain View management dashboard with your credentials
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;